import { useTu } from "@src/appV2/i18n/useTu";
import { Box, Button } from "@mui/material";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { Text } from "@src/appV2/redesign/components/Text";
import { TextInput } from "@src/appV2/redesign/components/TextInput";
import { isDefined } from "@clipboard-health/util-ts";
import { type FormEvent, useEffect, useRef, useState } from "react";

const OTP_CODE_LENGTH = 8;

interface OtpEntryFormProps {
  email: string;
  loading: boolean;
  errorMessage?: string;
  /** Whether the resend cooldown is currently active. */
  isResendCoolingDown: boolean;
  /** Seconds left on the resend cooldown; shown in the button label. */
  resendSecondsRemaining: number;
  /** Whether resend should be the primary recovery action. */
  isResendPrioritized?: boolean;
  /** Seconds left before the current code expires. */
  codeSecondsRemaining?: number;
  /** Whether the current code can no longer be submitted. */
  isCodeExpired?: boolean;
  /** Whether the current code submit action should be blocked. */
  isSubmitBlocked?: boolean;
  onSubmit: (code: string) => Promise<void>;
  onResend: () => void;
  onBack: () => void;
}

/** Group the raw digits as `1234 5678` for display. */
function formatOtp(rawDigits: string): string {
  if (rawDigits.length <= 4) {
    return rawDigits;
  }
  return `${rawDigits.slice(0, 4)} ${rawDigits.slice(4)}`;
}

function formatCountdown(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
}

/**
 * Code-entry screen shown after the user requests an email OTP. The user types
 * the 8-digit code from the email here, on the same browser that requested it.
 * The input stores raw digits and displays them grouped as `1234 5678`.
 */
export function OtpEntryForm(props: OtpEntryFormProps): React.ReactElement {
  const { tu } = useTu("login");
  const {
    email,
    loading,
    errorMessage,
    isResendCoolingDown,
    resendSecondsRemaining,
    isResendPrioritized = false,
    codeSecondsRemaining,
    isCodeExpired = false,
    isSubmitBlocked = isCodeExpired,
    onSubmit,
    onResend,
    onBack,
  } = props;

  const [code, setCode] = useState("");
  const formRef = useRef<HTMLFormElement>(null);

  const isCodeComplete = code.length === OTP_CODE_LENGTH;
  const shouldPrioritizeResend = isResendPrioritized || isSubmitBlocked;
  const resendLabel = isResendCoolingDown
    ? tu("Resend code in {{seconds}}s", { values: { seconds: resendSecondsRemaining } })
    : tu("Resend code");
  const instructionText = (() => {
    if (isSubmitBlocked) {
      return tu("We sent an 8-digit code to {{email}}.", {
        values: { email },
      });
    }

    if (isDefined(codeSecondsRemaining)) {
      return tu(
        "We sent an 8-digit code to {{email}}. The code expires in {{timeRemaining}}. Enter it below to sign in.",
        {
          values: { email, timeRemaining: formatCountdown(codeSecondsRemaining) },
        }
      );
    }

    return tu("We sent an 8-digit code to {{email}}. Enter it below to sign in.", {
      values: { email },
    });
  })();

  useEffect(() => {
    if (!isSubmitBlocked || !shouldPrioritizeResend || isResendCoolingDown || loading) {
      return;
    }

    formRef.current?.querySelector<HTMLButtonElement>("[data-resend-code-button]")?.focus();
  }, [isSubmitBlocked, shouldPrioritizeResend, isResendCoolingDown, loading]);

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!isCodeComplete || isSubmitBlocked) {
      return;
    }
    await onSubmit(code);
  }

  return (
    <Box
      component="form"
      ref={formRef}
      sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 5 }}
      onSubmit={(event) => {
        void handleSubmit(event);
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <Text sx={{ color: "primary.main", fontSize: "1.25rem" }}>✓</Text>
        <Text bold variant="h5" sx={{ color: "base.base" }}>
          {isSubmitBlocked ? tu("Get a new code") : tu("Enter your code")}
        </Text>
      </Box>
      <Text variant="body1" sx={{ color: "base.muted", textAlign: "center" }}>
        {instructionText}
      </Text>
      {!isSubmitBlocked && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, width: "100%" }}>
          <Text semibold variant="body2" sx={{ color: "base.muted" }}>
            {tu("Verification code")}
          </Text>
          <TextInput
            fullWidth
            required
            type="text"
            placeholder="0000 0000"
            value={formatOtp(code)}
            disabled={loading}
            autoComplete="one-time-code"
            inputProps={{
              "aria-label": tu("Verification code"),
              inputMode: "numeric",
              pattern: "[0-9 ]*",
              // 8 digits + the grouping space.
              maxLength: OTP_CODE_LENGTH + 1,
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                fontSize: "1.5rem",
                letterSpacing: "0.4rem",
                textAlign: "center",
                backgroundColor: "background.standout",
              },
            }}
            onChange={(event) => {
              setCode(event.target.value.replaceAll(/\D/g, "").slice(0, OTP_CODE_LENGTH));
            }}
          />
        </Box>
      )}
      {errorMessage && (
        <Text variant="body2" role="alert" sx={{ color: "error.main", textAlign: "center" }}>
          {errorMessage}
        </Text>
      )}
      {shouldPrioritizeResend && (
        <LoadingButton
          fullWidth
          type="button"
          variant="primary"
          size="large"
          isLoading={loading}
          disabled={isResendCoolingDown}
          data-resend-code-button
          onClick={onResend}
        >
          {resendLabel}
        </LoadingButton>
      )}
      {!isSubmitBlocked && (
        <LoadingButton
          fullWidth
          type="submit"
          variant={shouldPrioritizeResend ? "secondary" : "primary"}
          size="large"
          isLoading={loading}
          disabled={!isCodeComplete}
        >
          {tu("Sign in")}
        </LoadingButton>
      )}
      {!shouldPrioritizeResend && (
        <LoadingButton
          fullWidth
          type="button"
          variant="secondary"
          size="large"
          isLoading={false}
          disabled={loading || isResendCoolingDown}
          onClick={onResend}
        >
          {resendLabel}
        </LoadingButton>
      )}
      <Button
        variant="text"
        disabled={loading}
        sx={{ textTransform: "none", color: "primary.main" }}
        onClick={onBack}
      >
        {tu("Go back to login page")}
      </Button>
    </Box>
  );
}
